import { useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { useMe, useSignOut } from '../session.js';
import { LanguageSwitch } from './LanguageSwitch.js';
import { ShieldIcon } from './icons.js';
import { Button, LinkButton, LoadError, Loading, Notice } from './ui.js';

function SkipLink() {
  const { t } = useTranslation();
  return (
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-10 focus:rounded focus:bg-white focus:px-3 focus:py-2"
    >
      {t('app.skipToContent')}
    </a>
  );
}

/** Logo; `onDark` on the navy header and panel of the new design (T-47). */
function Brand({ onDark = false }: { onDark?: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={`flex items-center gap-2 text-lg font-bold ${onDark ? 'text-white [&>svg]:text-blue-400' : 'text-blue-800'}`}
    >
      <ShieldIcon />
      {t('app.name')}
    </span>
  );
}

const NAVY = 'bg-[#0b1b3f]';

/** The brand panel beside the login and registration forms: what the product does, at a glance. */
function BrandPanel() {
  const { t } = useTranslation();
  const points = [t('public.point1'), t('public.point2'), t('public.point3')];
  return (
    <div className="flex flex-col gap-8">
      <div>
        <p className="text-3xl leading-tight font-bold text-white text-balance">
          {t('public.tagline')}
        </p>
        <p className="mt-3 text-slate-300">{t('public.lead')}</p>
      </div>
      <ul className="flex flex-col gap-3">
        {points.map((point) => (
          <li key={point} className="flex items-start gap-3 text-slate-100">
            <span
              aria-hidden="true"
              className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500/30 text-blue-200"
            >
              ✓
            </span>
            {point}
          </li>
        ))}
      </ul>
      <div
        aria-hidden="true"
        className="flex items-center gap-4 rounded-2xl border border-white/15 bg-white/5 p-4"
      >
        <svg viewBox="0 0 120 120" className="h-20 w-20 shrink-0">
          <circle
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke="#ffffff"
            strokeOpacity="0.14"
            strokeWidth="12"
          />
          <circle
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke="#16a34a"
            strokeWidth="12"
            strokeLinecap="round"
            strokeDasharray="273 314.2"
            transform="rotate(-90 60 60)"
          />
          <text x="60" y="70" textAnchor="middle" fill="#ffffff" fontSize="36" fontWeight="800">
            B
          </text>
        </svg>
        <div className="text-sm text-slate-300">
          <p className="font-semibold text-white">{t('public.sample')}</p>
          <p>{t('public.sampleText')}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * The public website the app belongs to: SITE_URL of the build, else the app host without "app."
 * (app.guard.findyour.dance → guard.findyour.dance). Null where there is none (local development).
 */
export function landingUrl(location: Pick<Location, 'protocol' | 'hostname'> = window.location) {
  if (__SITE_URL__) return `${__SITE_URL__}/`;
  if (!location.hostname.startsWith('app.')) return null;
  return `${location.protocol}//${location.hostname.slice(4)}/`;
}

/**
 * Login, registration and link pages in the design of the landing page (T-47): a navy brand panel
 * beside the form card on wide screens, a navy header above it on phones. The Quick-Check result
 * (wide) keeps the navy header only.
 */
export function PublicLayout({ wide = false }: { wide?: boolean }) {
  const me = useMe();
  const { t } = useTranslation();
  // Signed out, "/" of the app only leads to the login: the logo goes back to the website instead.
  const site = me.data ? null : landingUrl();
  const header = (
    <div className="flex items-center justify-between gap-4">
      {site ? (
        <a href={site} aria-label={t('nav.toWebsite')}>
          <Brand onDark />
        </a>
      ) : (
        <Link to="/">
          <Brand onDark />
        </Link>
      )}
      <LanguageSwitch signedIn={Boolean(me.data)} />
    </div>
  );
  if (wide)
    return (
      <div className="flex min-h-screen flex-col bg-slate-50">
        <SkipLink />
        <header className={`${NAVY} px-4 py-3 sm:px-8`}>{header}</header>
        <main id="main" className="mx-auto w-full max-w-3xl flex-1 px-4 py-8">
          <Outlet />
        </main>
      </div>
    );
  return (
    <div className="flex min-h-screen flex-col bg-slate-50 lg:flex-row">
      <SkipLink />
      <header
        className={`${NAVY} bg-[radial-gradient(40rem_20rem_at_90%_-10%,rgba(29,78,216,0.8),transparent_60%)] px-4 py-4 sm:px-8 lg:flex lg:w-[44%] lg:flex-col lg:gap-16 lg:px-12 lg:py-10`}
      >
        {header}
        <div className="hidden lg:block">
          <BrandPanel />
        </div>
      </header>
      <main id="main" className="flex flex-1 items-start justify-center px-4 py-8 lg:items-center">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function VerifyBanner() {
  const { t } = useTranslation();
  const resend = useMutation({ mutationFn: () => unwrap(api.POST('/auth/resend-verification')) });
  return (
    <div className="border-b border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 sm:px-8">
      <p>{t('banner.verify')}</p>
      {resend.isSuccess ? (
        <p role="status" className="font-semibold">
          {t('banner.sent')}
        </p>
      ) : (
        <Button variant="link" busy={resend.isPending} onClick={() => resend.mutate()}>
          {t('banner.resend')}
        </Button>
      )}
      {resend.isError ? <p role="alert">{errorMessage(resend.error, t)}</p> : null}
    </div>
  );
}

/**
 * "Business kostenlos testen – noch 13 Tage" (v2 §14): while the trial runs; a paid plan ends
 * the trial (checkout clears trialEndsAt), and so does the worker on the last day.
 */
export function TrialBanner({
  trialEndsAt,
  now = new Date(),
}: {
  trialEndsAt: string | null;
  now?: Date;
}) {
  const { t } = useTranslation();
  if (!trialEndsAt) return null;
  const left = Math.ceil((Date.parse(trialEndsAt) - now.getTime()) / 86_400_000);
  if (left <= 0) return null;
  return (
    <div
      className={`border-b px-4 py-2 sm:px-8 print:hidden ${left <= 3 ? 'border-amber-300 bg-amber-50 text-amber-950' : 'border-blue-200 bg-blue-50 text-blue-950'}`}
    >
      <div className="mx-auto flex max-w-[120rem] flex-wrap items-center justify-between gap-2">
        <p className="font-medium">{t('banner.trial', { count: left })}</p>
        <Link
          to="/billing"
          className="font-semibold underline underline-offset-2 hover:no-underline"
        >
          {t('banner.trialCta')}
        </Link>
      </div>
    </div>
  );
}

/** Signed-in area: navigation (two levels at most, PRD §5), banner, content. */
export function AppLayout() {
  const { t } = useTranslation();
  const me = useMe();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const location = useLocation();

  if (me.isPending) return <Loading />;
  if (me.isError)
    return <LoadError message={errorMessage(me.error, t)} onRetry={() => void me.refetch()} />;
  if (!me.data)
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );

  const link = ({ isActive }: { isActive: boolean }) =>
    `inline-flex min-h-11 w-full items-center justify-center rounded-md px-3 font-semibold sm:w-auto ${isActive ? 'bg-white text-slate-900' : 'text-slate-100 hover:bg-white/10'}`;
  return (
    <div className="flex min-h-screen flex-col">
      <SkipLink />
      <header className={`${NAVY} print:hidden`}>
        <div className="mx-auto flex max-w-[120rem] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-8">
          <Link to="/">
            <Brand onDark />
          </Link>
          <nav aria-label={t('nav.label')} className="order-3 w-full sm:order-2 sm:w-auto">
            <ul className="flex flex-wrap gap-1 [&>li]:flex-auto sm:[&>li]:flex-none">
              <li>
                <NavLink to="/" end className={link}>
                  {t('nav.overview')}
                </NavLink>
              </li>
              <li>
                <NavLink to="/requests" className={link}>
                  {t('nav.requests')}
                </NavLink>
              </li>
              <li>
                <NavLink to="/notifications" className={link}>
                  {t('nav.notifications')}
                </NavLink>
              </li>
              <li>
                <NavLink to="/billing" className={link}>
                  {t('nav.billing')}
                </NavLink>
              </li>
              <li>
                <NavLink to="/account" className={link}>
                  {t('nav.account')}
                </NavLink>
              </li>
              {me.data.user.isOperator ? (
                <li>
                  <NavLink to="/admin" className={link}>
                    {t('nav.admin')}
                  </NavLink>
                </li>
              ) : null}
            </ul>
          </nav>
          <div className="order-2 flex w-full items-center justify-between gap-2 sm:order-3 sm:w-auto">
            <LanguageSwitch signedIn className="flex-1 sm:flex-none" />
            <Button
              variant="secondary"
              onClick={() =>
                void signOut().then(() => {
                  void navigate('/login');
                })
              }
            >
              {t('nav.logout')}
            </Button>
          </div>
        </div>
      </header>
      {me.data.user.emailVerified ? null : <VerifyBanner />}
      <TrialBanner trialEndsAt={me.data.account.trialEndsAt} />
      <main id="main" className="mx-auto w-full max-w-[120rem] flex-1 px-4 py-6 sm:px-8">
        <Outlet />
      </main>
    </div>
  );
}

export function PageTitle({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <h1 className="text-2xl font-bold sm:text-3xl">{children}</h1>
      {actions}
    </div>
  );
}

export function NotFound() {
  const { t } = useTranslation();
  return (
    <main id="main" className="mx-auto max-w-md px-4 py-12">
      <h1 className="mb-2 text-2xl font-bold">{t('notFound.title')}</h1>
      <Notice kind="info">{t('notFound.text')}</Notice>
      <p className="mt-4">
        <LinkButton to="/" variant="primary">
          {t('notFound.home')}
        </LinkButton>
      </p>
    </main>
  );
}
